import Axios from "axios-observable";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ErrorResponse } from "@ntprsrf-frontend/service/src/responses/base.response";

type ErrorNotificationFn = (title: string, message: string, type?: any, timeout?: number) => void;

let abortController = new AbortController();
let errorNotification: ErrorNotificationFn | null = null;

export function setErrorNotification(notificationFn: ErrorNotificationFn) {
	errorNotification = notificationFn;
}

const backgroundRequestUrls = ['audit/notification-click', 'sftp/account/is-exist', 'important-announcement', 'notice'];
const ignoredRequestUrls = ['vdp-internet/cta-docs']

const errorHandler = (message: string, requestUrl: string = '', status: number = 403) => {
	const toastMessagesToBeSkipped = ['Token is not in a right format', 'Share Request Not Found', 'Service is Not Active']
	const requestUrlToBeSkipped: (string | RegExp)[] = ['hces/upload', 'hces/list/internal', 'track-and-trace/containerNumber', /(?<!report\/)permit-return\/query/, 'government-portal', 'fz/file/upload/eGuarantee/share-file-name', 'fz/file/upload/eGuarantee/share-file', 'cta/selection/validate', 'vdp-internet/documents/list/required', 'permit/detail/save/permit', "vdp-intranet/permit/detail/update/status", "vdp-internet/dashboard/permits/check-download", ...backgroundRequestUrls, ...ignoredRequestUrls];

	let skipRequestUrl = false;

	requestUrlToBeSkipped.forEach(v => {
		if(!skipRequestUrl) {
			skipRequestUrl = typeof v === 'string' ? requestUrl.includes(v) : v.test(requestUrl);
		}
	})

	if (!toastMessagesToBeSkipped.includes(message) && !skipRequestUrl) { 
		if (errorNotification) {
			errorNotification("Error", message);
		}
	}
};

export const getCookie = (cookieName: string) => {
	let cookie: {[key: string]: string} = {};
	document.cookie.split(';').forEach(function(el) {
	  let [key,value] = el.split('=');
	  cookie[key.trim()] = value;
	})
	return cookie[cookieName];
};

export const DOWNLOAD_CONFIG: AxiosRequestConfig = {
	responseType: "blob",
	validateStatus: (s: number) => [200, 201, 400].includes(s),
}

export function credentialInterceptor() {
	Axios.defaults.withCredentials = true;
}

export const requestFulfilledHandler = (config: AxiosRequestConfig) => {
	config.url = config.url
		?.replace(/\/+/g, "/")
		.replace(/^https:\//, "https://");
	config.headers = {
		...config.headers,
		'Cache-control': 'no-store',
		'Pragma': 'no-cache'
	}
	const csrfToken = getCookie('ntp2-csrf');
	if (csrfToken) {
		config.headers['X-CSRF-TOKEN'] = csrfToken;
	}
	if (abortController.signal.aborted) {
		abortController = new AbortController();
	}
	config.signal = abortController.signal;
	return config;
}

export const requestRejectedHandler = (error: AxiosError) => Promise.reject(error);

export const responseFulfilledHandler = async (res: AxiosResponse) => {
	if (res.config.url?.endsWith('logout')) {
		abortController.abort();
		abortController = new AbortController();
	}
	if (res.status === 400 && res.config.responseType === "blob") {
		const errRes: ErrorResponse = JSON.parse(await res.data.text());
		errorHandler(errRes.message, res.config.url);
		throw new AxiosError(errRes.message);
	}
	return res;
}

export const responseRejectedHandler = (err: Error | AxiosError<ErrorResponse>) => {
	if (axios.isCancel(err)) {
		return {
			data: {
				message: '',
				pageNumber: 0,
				pageSize: 0,
				total: 0,
				totalPage: 0,
				data: []
			}
		}
	} else if (axios.isAxiosError(err)) {
		const status = (err as AxiosError<ErrorResponse>).request?.status;
		const url = (err as AxiosError<ErrorResponse>).request?.responseURL;
		const message =
			(err as AxiosError<ErrorResponse>).response?.data.message ??
			(err as AxiosError<ErrorResponse>).message;
		const isLogout401 = status === 401 && url.includes("logout");
		const isBackgroundRequest = backgroundRequestUrls.some(v => url.includes(v));
		const isIgnoredRequest = ignoredRequestUrls.some(v => url.includes(v));
		if (!isLogout401 && !isBackgroundRequest && !isIgnoredRequest) {
			errorHandler(message, url, status);
			
			const currentRoute = window.location.hash;
			const isAlreadyOnErrorPage = currentRoute.includes('/403') || currentRoute.includes('/404') || currentRoute.includes('/503');
			
			if (!isAlreadyOnErrorPage) {
				if (status === 404) {
					window.location.hash = '#/404';
				} else if (status === 503) {
					window.location.hash = '#/503';
				} else if (status === 403) {
					window.location.hash = '#/403';
				}
			}
		}
	}
	throw err;
}

export function normalizeRequestInterceptor() {
	return Axios.interceptors.request.use(
		requestFulfilledHandler,
		requestRejectedHandler,
	);
}

export function handleErrorInterceptor() {
	return Axios.interceptors.response.use(
		responseFulfilledHandler,
		responseRejectedHandler,
	);
}
